import { useTranslation } from "react-i18next";
import type { RemixiconComponentType } from "@remixicon/react";
import {
    RiAlertLine,
    RiMap2Line,
    RiNotificationLine,
    RiRouteLine,
    RiUserLocationLine,
    RiWrenchLine,
} from "@remixicon/react";
import { Picture } from "~/components/ui/picture";
import GeoZone from "~/assets/icons/GeoZone";
import EngineOutline from "~/assets/icons/EngineOutline";
import { imageUrl } from "~/lib/image-url";

// Plain public/ paths so Picture can serve the AVIF/WebP siblings. Flattened design exports
// per breakpoint (the mobile cards carry a menu icon the desktop ones don't).
const analyticsImg = imageUrl("/images/fleet/fleet-analytics-tablet-man.png");
const analyticsImgMobile = imageUrl("/images/fleet/fleet-analytics-tablet-man-mobile.png");

// Column-major: the first four fill the left column, the rest the right.
const FEATURES: { id: string; Icon: RemixiconComponentType }[] = [
    { id: "map", Icon: RiMap2Line },
    { id: "route", Icon: RiRouteLine },
    { id: "driver", Icon: RiUserLocationLine },
    { id: "alerts", Icon: RiNotificationLine },
    { id: "geo_zones", Icon: GeoZone as RemixiconComponentType },
    { id: "impact", Icon: RiAlertLine },
    { id: "maintenance", Icon: RiWrenchLine },
    { id: "diagnostic", Icon: EngineOutline as RemixiconComponentType },
];

// Image beside copy and a two-column feature list; copy first on mobile. The top padding
// is the design's 72px gap to the vehicle-view panel above it on desktop.
// Must render inside the .fleet token scope.
export function FleetPerformance() {
    const { t } = useTranslation("fleet");

    return (
        <section aria-labelledby="fleet-performance-title" className="py-8 lg:pt-[72px] lg:pb-0">
            <div className="wrap flex flex-col items-center gap-6 lg:flex-row-reverse lg:justify-between lg:gap-8">
                <div className="flex min-h-[320px] w-full flex-col gap-6 lg:min-h-0 lg:max-w-[479px] lg:flex-1 lg:gap-8">
                    <div className="flex flex-col gap-2 lg:gap-4">
                        <h2
                            id="fleet-performance-title"
                            className="font-display text-text-heading max-w-[277px] text-xl leading-[1.15] font-extrabold md:max-w-none lg:text-4xl lg:leading-[1.1]"
                        >
                            {t("performance.title")}
                        </h2>
                        <p className="text-text-body-secondary lg:text-md max-w-[321px] text-sm leading-5 md:max-w-none lg:leading-[1.1]">
                            {t("performance.body")}
                        </p>
                    </div>
                    {/* Desktop items are the design's fixed 211px wide, shrinking if the copy column is narrower. */}
                    <ul className="text-text-body grid grid-flow-col grid-rows-4 justify-start gap-x-6 gap-y-3 text-sm leading-5 lg:auto-cols-[minmax(auto,211px)] lg:gap-x-10 lg:gap-y-4 lg:text-base lg:leading-[1.5]">
                        {FEATURES.map(({ id, Icon }) => (
                            <li key={id} className="flex items-center gap-3">
                                <Icon aria-hidden className="text-icon-primary size-6 shrink-0" />
                                {t(`performance.features.${id}`)}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="relative aspect-[328/310] w-[328px] max-w-none shrink-0 lg:aspect-[606/573] lg:w-[51.2%] lg:max-w-[606px]">
                    <Picture
                        src={analyticsImg}
                        mobileSrc={analyticsImgMobile}
                        desktopMedia="(min-width: 1024px)"
                        alt={t("performance.image_alt")}
                        loading="lazy"
                        // Both exports run ~6.7% past their frame on the right (transparent).
                        className="absolute top-0 left-0 h-full w-[106.7%] max-w-none"
                    />
                </div>
            </div>
        </section>
    );
}

export default FleetPerformance;
